import React, { useEffect, useRef } from "react";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ClearIcon from "@mui/icons-material/Clear";
import GridViewIcon from "@mui/icons-material/GridView";
import TableViewIcon from "@mui/icons-material/TableView";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Input,
  MenuItem,
  FormControl,
  TextField,
  InputAdornment,
  makeStyles,
} from "@material-ui/core";
import { useCaseListContext } from "../../context/CaseListContext";
import { useCaseSearch } from "../../hooks/Cases/useCaseSearch";
import FilterDrawer from "../../components/Cases/FilterDrawer";
import {
  PrimaryButton,
  StyledIconButton,
} from "../../components/shared/helpers/GlobalStylesv5";
import { Tooltip } from "@mui/material";
import _ from "lodash";
import { tokens } from "../../theme/tokens";

const useStyle = makeStyles((theme) => ({
  topBar: {
    height: "50px",
    padding: "0 2% 0 2%",
    display: "flex",
    alignItems: "center",
  },
  heading: {
    fontSize: tokens.type.subheading.size,
    color: tokens.color.text.primary,
    fontWeight: tokens.type.subheading.weight,
    lineHeight: 1.2,
  },
  subtitle: {
    marginTop: "2px",
    fontSize: tokens.type.caption.size,
    color: tokens.color.text.secondary,
  },
  seg: {
    display: "inline-flex",
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.sm,
    overflow: "hidden",
    backgroundColor: tokens.color.surface.default,
  },
  segBtn: {
    appearance: "none",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    font: "inherit",
    fontSize: tokens.type.dense.size,
    fontWeight: tokens.type.denseStrong.weight,
    color: tokens.color.text.secondary,
    padding: `${tokens.space[2]} ${tokens.space[3]}`,
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.space[1],
    transition: `background-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    "&:not(:first-child)": {
      borderLeft: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    },
    "&:hover": { backgroundColor: tokens.color.surface.hover },
  },
  segBtnActive: {
    backgroundColor: tokens.color.brand.primaryAction,
    color: tokens.color.brand.onPrimary,
    "&:hover": { backgroundColor: tokens.color.brand.primaryActionHover },
  },
  buttonRow: {
    display: "flex",
    flexDirection: "row",
    columnGap: "15px",
    marginTop: "1vh",
    marginBottom: "1vh",
  },
  buttonRowTabButton: {
    boxShadow: "none",
    backgroundColor: tokens.color.surface.sunken,
    borderRadius: tokens.radius.sm,
    textTransform: "capitalize",
    marginRight: "1em",
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    paddingLeft: "10%",
    paddingRight: "2%",
  },
  input: {
    flex: "1 1 320px",
    minWidth: "220px",
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    "& .MuiInputBase-input": {
      color: tokens.color.text.primary,
      fontSize: tokens.type.dense.size,
    },
    borderRadius: tokens.radius.sm,
    height: "38px",
    paddingLeft: tokens.space[2],
    backgroundColor: tokens.color.surface.default,
    transition: `border-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    "&:hover": { borderColor: tokens.color.border.strong },
    "&.Mui-focused": { borderColor: tokens.color.focus.ring },
  },
  searchIcon: {
    color: tokens.color.text.disabled,
    fontSize: "18px",
    marginRight: tokens.space[1],
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: tokens.space[3],
    padding: `${tokens.space[3]} ${tokens.space[4]} ${tokens.space[2]}`,
    flexWrap: "wrap",
  },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[2],
    padding: `${tokens.space[2]} ${tokens.space[4]} ${tokens.space[3]}`,
    flexWrap: "wrap",
  },
  iconGroup: {
    display: "inline-flex",
    gap: tokens.space[1],
  },
  clearIcon: {
    color: tokens.color.text.primary,
    fontSize: "20px",
    marginRight: "8px",
    cursor: "pointer",
  },
  filterIconOn: {
    "&:hover": {
      color: tokens.color.brand.primaryAction,
    },
  },
  filterSelect: {
    width: "165px",
    textAlign: "left",
    marginRight: "1em",
    "& .MuiSelect-selectMenu": {
      display: "flex",
      alignItems: "center",
    },
  },
  sortFormControl: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginTop: "5px",
    marginBottom: "5px",
    minWidth: 120,
  },
  sortRow: {
    display: "flex",
    justifyContent: "center",
    columnGap: "15px",
    marginLeft: "18.5vw",
  },
  sortCard: {
    flex: "1 0 30%",
    zIndex: 15,
    maxWidth: "40%",
    minWidth: "25%",
    backgroundColor: tokens.color.surface.default,
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.md,
    boxShadow: "none",
    display: "flex",
    flexDirection: "row",
    fontSize: "1em",
    justifyContent: "space-around",
    position: "absolute",
    paddingRight: "15px",
    paddingLeft: "15px",
    paddingBottom: "10px",
  },
  filterCross: {
    alignItems: "center",
    marginRight: "1rem",
    marginTop: "1.5rem",
    cursor: "pointer",
    "&:hover": {
      color: tokens.color.status.error,
    },
  },
  formikInput: {
    fontSize: "1em",
    marginLeft: "1vh",
    width: "100%",
    textAlign: "right",
    display: "flex",
    alignItems: "center",
    "& .MuiInputBase-input": {
      textAlign: "right",
      display: "flex",
      alignItems: "center",
    },
  },
  viewSelectContainer: {
    width: "132px",
  },
  refreshIcon: {
    color: tokens.color.text.primary,
    fontSize: "20px !important",
    "&:hover": {
      color: tokens.color.brand.primaryAction,
    },
  },
}));

const sortOptions = [
  { title: "Created At", key: "created_at", type: "date" },
  { title: "Follow Up Date", key: "follow_up", type: "date" },
  { title: "Due Date", key: "due_date", type: "date" },
  { title: "Invoice Date", key: "invoice_date", type: "date" },
  { title: "File Number", key: "file_no", type: "input" },
  { title: "Client", key: "client", type: "select" },
  { title: "User", key: "user", type: "select" },
];

function CaseListTopBar() {
  const classes = useStyle();
  const wrapperRef = useRef(null);

  const {
    list,
    searchValue,
    setSearchValue,
    filterClicked,
    setFilterClicked,
    filters,
    setFilters,
    setPendingFilters,
    setPage,
    caseView,
    setCaseView,
    sort,
    setSort,
    sortClicked,
    setSortClicked,
    resetToDefaults,
    getDefaultFilters,
  } = useCaseListContext();

  const { handleSearchSubmit } = useCaseSearch();

  const onChangeSearchHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchValue(event.target.value);
  };

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (
        wrapperRef.current &&
        !(wrapperRef.current as any).contains(event.target)
      ) {
        if (
          !event.target.classList.contains("menu-item") &&
          !event.target.classList.contains("MuiButtonBase-root")
        ) {
          setSortClicked(false);
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOpenFilterDrawer = () => {
    // Initialize pending filters with current applied filters
    setPendingFilters([...filters]);
    setFilterClicked(true);
  };

  const handleResetToDefaults = () => {
    resetToDefaults();
  };

  return (
    <div>
      {/* Header row: title + count · segmented view toggle */}
      <div className={classes.headerRow}>
        <div>
          <div className={classes.heading}>Cases</div>
          <div className={classes.subtitle}>
            {list?.length ?? 0} {(list?.length ?? 0) === 1 ? "case" : "cases"}
            {filters?.length ? " · filtered" : ""}
          </div>
        </div>
        <div className={classes.seg} role="group" aria-label="View">
          <button
            type="button"
            aria-pressed={caseView === "grid"}
            className={`${classes.segBtn} ${
              caseView === "grid" ? classes.segBtnActive : ""
            }`}
            onClick={() => setCaseView("grid")}
          >
            <GridViewIcon style={{ fontSize: "18px" }} />
            Cards
          </button>
          <button
            type="button"
            aria-pressed={caseView === "table"}
            className={`${classes.segBtn} ${
              caseView === "table" ? classes.segBtnActive : ""
            }`}
            onClick={() => setCaseView("table")}
          >
            <TableViewIcon style={{ fontSize: "18px" }} />
            Table
          </button>
        </div>
      </div>

      {/* Toolbar row: search · grouped actions */}
      <div className={classes.toolbarRow}>
        <Input
          placeholder={"Search by file #, subject, client…"}
          type={"text"}
          onChange={onChangeSearchHandler}
          value={searchValue}
          className={classes.input}
          disableUnderline
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon className={classes.searchIcon} />
            </InputAdornment>
          }
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              setPage(1);
              handleSearchSubmit();
            }
          }}
          endAdornment={
            <InputAdornment position="end">
              {searchValue !== "" ? (
                <ClearIcon
                  className={classes.clearIcon}
                  onClick={() => {
                    setSearchValue("");
                    setFilters([]);
                    handleSearchSubmit();
                  }}
                />
              ) : (
                <></>
              )}
            </InputAdornment>
          }
        />

        <PrimaryButton
          onClick={() => {
            setPage(1);
            handleSearchSubmit();
          }}
        >
          Search
        </PrimaryButton>

        <div className={classes.iconGroup}>
          <Tooltip title="Filter Cases" placement="bottom">
            <StyledIconButton
              onClick={handleOpenFilterDrawer}
              style={
                filterClicked || filters.length
                  ? { backgroundColor: tokens.color.brand.primaryAction, color: tokens.color.brand.onPrimary }
                  : undefined
              }
            >
              <FilterAltOutlinedIcon />
            </StyledIconButton>
          </Tooltip>
          <Tooltip title="Sort Cases" placement="bottom">
            <StyledIconButton
              onClick={() => {
                setSortClicked(!sortClicked);
              }}
              style={
                sortClicked || sort?.value != null
                  ? { backgroundColor: tokens.color.brand.primaryAction, color: tokens.color.brand.onPrimary }
                  : undefined
              }
            >
              <SwapVertIcon />
            </StyledIconButton>
          </Tooltip>

          {!_.isEqual(getDefaultFilters(), filters) && (
            <Tooltip title="Reset to default filters" placement="bottom">
              <StyledIconButton onClick={handleResetToDefaults}>
                <RefreshIcon />
              </StyledIconButton>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Filter Drawer */}
      <FilterDrawer
        open={filterClicked}
        onClose={() => setFilterClicked(false)}
      />

      {/* Sort Section */}
      {sortClicked && (
        <div ref={wrapperRef} className={classes.sortRow}>
          <div className={classes.sortCard}>
            <span
              className={classes.filterCross}
              onClick={() => {
                setSort({ value: null, type: "ASC" });
                localStorage.setItem(
                  "sort",
                  JSON.stringify({ value: null, type: "ASC" }),
                );
                setSortClicked(false);
              }}
            >
              <ClearIcon />
            </span>
            <FormControl className={classes.sortFormControl}>
              <TextField
                select
                label="Select"
                value={sort?.value || ""}
                className={classes.filterSelect}
                onChange={(e) => {
                  setSort({ value: e.target.value, type: "ASC" });
                }}
              >
                {sortOptions.map((e, key) => {
                  return (
                    <MenuItem className="menu-item" key={e.key} value={e.key}>
                      {e.title}
                    </MenuItem>
                  );
                })}
              </TextField>
            </FormControl>
            <FormControl className={classes.sortFormControl}>
              <TextField
                select
                label="Select Sort Type"
                value={sort?.type}
                className={classes.filterSelect}
                onChange={(e) => {
                  setSort({ ...sort, type: e.target.value as "ASC" | "DESC" });
                }}
              >
                <MenuItem className="menu-item" key={"ASC"} value={"ASC"}>
                  ASC
                </MenuItem>
                <MenuItem className="menu-item" key={"DESC"} value={"DESC"}>
                  DESC
                </MenuItem>
              </TextField>
            </FormControl>
          </div>
        </div>
      )}

      <br />
    </div>
  );
}

export default CaseListTopBar;
